/**
 * RetailerEstimates (/retaileradmin/estimates)
 *
 * Paginated, searchable, sortable list of the estimates in the retailer's
 * view: saved by its approved members, shared to its stores, or created on
 * its branded portal. Viewers open and comment; managers also edit the
 * customer / project details.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronUp, ChevronDown, ExternalLink, Eye, MessageSquare, CalendarDays, TrendingUp, ClipboardList, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { useRetailerContext } from './RetailerAdminContext';
import { RetailerEstimateEditModal, type RetailerEstimateEditable } from './RetailerEstimateEditModal';
import { fetchRetailerEstimates, type RetailerEstimateRow, type EstimateStats, type Pagination } from '../../services/retailerAdminApi';
import { formatDateTime, locationLabel, modeAndSpaceLabel } from '../../utils/estimateLabels';

type SortField = 'id' | 'first_name' | 'estimate_name' | 'created_at' | 'city' | 'views' | 'comments';

export const RetailerEstimates: React.FC = () => {
  const { t, i18n } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const [estimates, setEstimates] = useState<RetailerEstimateRow[]>([]);
  const [stats, setStats] = useState<EstimateStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, per_page: 25, total: 0, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<SortField>('created_at');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editTarget, setEditTarget] = useState<RetailerEstimateEditable | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await fetchRetailerEstimates({ page: pagination.page, perPage, search, sort, order });
      setEstimates(data.estimates);
      setPagination(data.pagination);
      setStats(data.stats);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, perPage, search, sort, order]);

  useEffect(() => { load(); }, [load, ctx.retailerId]);

  const handleSort = (field: SortField) => {
    if (sort === field) setOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    else { setSort(field); setOrder('DESC'); }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const goToPage = (page: number) => setPagination(prev => ({ ...prev, page }));

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sort !== field) return <ChevronDown size={12} className="opacity-25" />;
    return order === 'ASC' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };
  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ field, children, className }) => (
    <th className={`user-select-none ${className || ''}`} style={{ cursor: 'pointer' }} onClick={() => handleSort(field)}>
      <span className="d-inline-flex align-items-center gap-1">{children}<SortIcon field={field} /></span>
    </th>
  );

  const pageNumbers: number[] = [];
  const maxPages = 7;
  let startPage = Math.max(1, pagination.page - Math.floor(maxPages / 2));
  const endPage = Math.min(pagination.total_pages, startPage + maxPages - 1);
  if (endPage - startPage < maxPages - 1) startPage = Math.max(1, endPage - maxPages + 1);
  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

  const colCount = ctx.canManage ? 9 : 8;
  const statCards = stats ? [
    { key: 'today', value: stats.today, icon: CalendarDays, color: 'primary' },
    { key: 'yesterday', value: stats.yesterday, icon: CalendarDays, color: 'secondary' },
    { key: 'thisWeek', value: stats.this_week, icon: TrendingUp, color: 'success' },
    { key: 'lastWeek', value: stats.last_week, icon: TrendingUp, color: 'info' },
    { key: 'thisMonth', value: stats.this_month, icon: ClipboardList, color: 'warning' },
    { key: 'lastMonth', value: stats.last_month, icon: ClipboardList, color: 'secondary' },
    { key: 'total', value: stats.total, icon: ClipboardList, color: 'dark' },
  ] : [];

  return (
    <div>
      {stats && (
        <div className="row g-2 mb-3">
          {statCards.map(c => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="col-6 col-sm-4 col-lg">
                <div className="bg-white rounded-3 border p-3 text-center h-100">
                  <Icon size={18} className={`text-${c.color} mb-1`} />
                  <div className="fw-bold fs-4 text-dark">{c.value}</div>
                  <div className="text-muted small">{t(`dashboard.stats.${c.key}`)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <form onSubmit={handleSearch} className="d-flex gap-2" style={{ maxWidth: 400, flex: 1 }}>
            <div className="input-group input-group-sm">
              <span className="input-group-text"><Search size={14} /></span>
              <input type="text" className="form-control" placeholder={t('estimates.searchPlaceholder')} value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-sm btn-primary">{t('estimates.search')}</button>
            {search && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setSearchInput(''); }}>{t('estimates.clear')}</button>
            )}
          </form>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="ra-per-page" className="small text-muted mb-0">{t('estimates.show')}</label>
            <select id="ra-per-page" className="form-select form-select-sm" style={{ width: 'auto' }} value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPagination(prev => ({ ...prev, page: 1 })); }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        {search && <div className="mt-2 small text-muted">{t('estimates.resultsFor', { query: search, count: pagination.total })}</div>}
      </div>

      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0">
            <thead className="table-tc-slate">
              <tr>
                <SortableHeader field="id">{t('estimates.columns.id')}</SortableHeader>
                <SortableHeader field="first_name">{t('estimates.columns.customer')}</SortableHeader>
                <SortableHeader field="estimate_name">{t('estimates.columns.project')}</SortableHeader>
                <SortableHeader field="city">{t('estimates.columns.location')}</SortableHeader>
                <SortableHeader field="created_at">{t('estimates.columns.date')}</SortableHeader>
                <SortableHeader field="views" className="text-center text-white"><Eye size={14} aria-label={t('estimates.columns.views')} /></SortableHeader>
                <SortableHeader field="comments" className="text-center text-white"><MessageSquare size={14} aria-label={t('estimates.columns.comments')} /></SortableHeader>
                {ctx.canManage && <th className="text-center text-white">{t('estimates.columns.edit')}</th>}
                <th className="text-center text-white">{t('estimates.columns.view')}</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr><td colSpan={colCount} className="text-center py-4 text-muted">{t('estimates.loading')}</td></tr>
              ) : failed ? (
                <tr><td colSpan={colCount} className="text-center py-4 text-danger">{t('estimates.loadFailed')}</td></tr>
              ) : estimates.length === 0 ? (
                <tr><td colSpan={colCount} className="text-center py-4 text-muted">{t('estimates.empty')}</td></tr>
              ) : estimates.map(est => (
                <tr key={est.id}>
                  <td className="small">{est.id}</td>
                  <td>
                    <div className="fw-semibold small">{est.first_name} {est.last_name}</div>
                    {est.email && <div className="text-muted" style={{ fontSize: '0.75em' }}>{est.email}</div>}
                    {est.phone && <div className="text-muted" style={{ fontSize: '0.75em' }}>{est.phone}</div>}
                  </td>
                  <td>
                    <div className="small">{est.estimate_name || <span className="text-muted fst-italic">{t('estimates.untitled')}</span>}</div>
                    {modeAndSpaceLabel(est.calculation_mode, est.space_type) && (
                      <div className="text-muted" style={{ fontSize: '0.75em' }}>{modeAndSpaceLabel(est.calculation_mode, est.space_type)}</div>
                    )}
                    {est.prepared_by_label && <div className="text-info" style={{ fontSize: '0.75em' }}>{t('estimates.preparedBy', { label: est.prepared_by_label })}</div>}
                  </td>
                  <td className="small">{locationLabel(est.city, est.region)}</td>
                  <td className="small text-nowrap">{formatDateTime(est.created_at, i18n.language)}</td>
                  <td className="text-center small"><span className={est.views > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.views}</span></td>
                  <td className="text-center small"><span className={est.comments > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.comments}</span></td>
                  {ctx.canManage && (
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-1"
                        title={t('estimates.editTitle')}
                        onClick={() => setEditTarget({ id: est.id, estimate_name: est.estimate_name, first_name: est.first_name, last_name: est.last_name, email: est.email })}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  )}
                  <td className="text-center">
                    <a href={`/estimate/${est.guid}?source=retailer`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 px-1" title={t('estimates.openTitle')}>
                      <ExternalLink size={14} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <RetailerEstimateEditModal
          isOpen={editTarget !== null}
          estimate={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={next => {
            setEstimates(prev => prev.map(e => (e.id === next.id
              ? { ...e, first_name: next.first_name, last_name: next.last_name, email: next.email, estimate_name: next.estimate_name }
              : e)));
          }}
        />

        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top">
            <div className="small text-muted">
              {t('estimates.showing', { from: (pagination.page - 1) * pagination.per_page + 1, to: Math.min(pagination.page * pagination.per_page, pagination.total), total: pagination.total })}
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => goToPage(1)}>{t('estimates.first')}</button></li>
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => goToPage(pagination.page - 1)}><ChevronLeft size={14} /></button></li>
                {pageNumbers.map(n => (
                  <li key={n} className={`page-item ${n === pagination.page ? 'active' : ''}`}><button className="page-link" onClick={() => goToPage(n)}>{n}</button></li>
                ))}
                <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}><button className="page-link" onClick={() => goToPage(pagination.page + 1)}><ChevronRight size={14} /></button></li>
                <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}><button className="page-link" onClick={() => goToPage(pagination.total_pages)}>{t('estimates.last')}</button></li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetailerEstimates;
